import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { ControllerManager } from '@gui/controller/ControllerManager';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { Box, Checkbox, Slider, Typography } from '@mui/material';
import type { ConvexHullDesignState, DeepPartial } from '@visualizer/types';
import { DesignSchemeController } from '../DesignSchemeView';

export interface ConvexHullGuiProps {
    setId: string;
    footprintId: string;
    schemeId: string;
}

export const ConvexHullView: React.FC<ConvexHullGuiProps> = ({ setId, footprintId, schemeId }) => {
    // Controllers:
    const designSchemesController = ControllerManager.getInstance().getDesignSchemeController();

    // Zustand Variables:
    const designSchemes = usePersistentStore((s) => s.designSchemes);
    const scheme = designSchemes.sets?.[setId]?.footprints?.[footprintId]?.designSchemes?.[schemeId];
    const convexHullState = scheme.convexHull;

    // Defaults:
    const defaults = designSchemes.defaults.convexHull;
    const active = convexHullState?.active ?? defaults.active;
    const transparent = convexHullState?.transparent ?? defaults.transparent;
    const concave = convexHullState?.concave ?? defaults.concave;
    const concaveAlpha = convexHullState?.concaveAlpha ?? defaults.concaveAlpha;

    // Handler:
    const updateConvexHull = (partial: DeepPartial<ConvexHullDesignState>) => {
        designSchemesController.updateDesignScheme(setId, footprintId, schemeId, {
            convexHull: partial,
        });
    };

    const formatOneDecimal = (value: number) => value.toFixed(1);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <CustomTooltip
                    mode="icon"
                    side="right"
                    title={
                        <Typography
                            sx={{
                                fontSize: '10.5px',
                                lineHeight: 1.4,
                                color: 'text.secondary',
                            }}
                        >
                            Generates an hull around the footprint's sensor points, producing a simplified enclosing geometry that captures
                            the overall spatial extent of the site.
                        </Typography>
                    }
                >
                    <Typography variant="subtitle1" align="center" sx={{ textAlign: 'center' }}>
                        Hull
                    </Typography>
                </CustomTooltip>
            </Box>
            <Box mt={1}>
                <DesignSchemeController schemeId={schemeId} footprintId={footprintId} setId={setId} />
            </Box>

            {active && (
                <>
                    <Box mt={1} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Checkbox
                            data-testid="ds-hull-transparent-checkbox"
                            size="small"
                            checked={transparent}
                            onChange={(e) => updateConvexHull({ transparent: e.target.checked })}
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Transparent</Typography>
                        <Checkbox
                            data-testid="ds-hull-concave-checkbox"
                            size="small"
                            checked={concave}
                            onChange={(e) => updateConvexHull({ concave: e.target.checked })}
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Concave</Typography>
                    </Box>

                    {concave && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                minWidth: 0,
                                mt: 1,
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                Concaveness
                            </Typography>

                            <Slider
                                data-testid="ds-hull-concaveness-slider"
                                className="panel-slider"
                                size="small"
                                value={concaveAlpha.value}
                                min={concaveAlpha.minValue}
                                max={concaveAlpha.maxValue}
                                step={0.1}
                                valueLabelDisplay="on"
                                valueLabelFormat={formatOneDecimal}
                                onChange={(_, value) => {
                                    const nextValue = Array.isArray(value) ? value[0] : value;
                                    updateConvexHull({
                                        concaveAlpha: { ...concaveAlpha, value: nextValue },
                                    });
                                }}
                                sx={{ flex: '1 1 0', minWidth: 0 }}
                            />
                            <Typography variant="body2" whiteSpace="nowrap" sx={{ ml: 0.5, textAlign: 'right', flex: '0 0 auto' }}>
                                [a]
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};
